import { useCallback, useRef, useState } from "react";
import type { VolumeKeyframe, TimelineAudioTrack, TimelineAction } from "@scroll/shared-types/timeline";

interface VolumeKeyframeEditorProps {
  track: TimelineAudioTrack;
  pixelsPerSecond: number;
  onDispatch: (action: TimelineAction) => void;
}

/**
 * SVG overlay on an audio track bar that shows the volume curve
 * and allows adding/dragging keyframe points.
 */
export default function VolumeKeyframeEditor({
  track,
  pixelsPerSecond,
  onDispatch,
}: VolumeKeyframeEditorProps) {
  const trackDuration = track.trimEnd - track.trimStart;
  if (trackDuration <= 0) return null;
  const width = trackDuration * pixelsPerSecond;
  const height = 28; // matches audio bar height

  const svgRef = useRef<SVGSVGElement>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const startValuesRef = useRef<VolumeKeyframe[]>([]);

  const keyframes = track.volumeKeyframes;

  // Build SVG polyline points for the volume curve
  const points = keyframes
    .map((kf) => {
      const x = (kf.time / trackDuration) * width;
      const y = (1 - kf.value) * height;
      return `${x},${y}`;
    })
    .join(" ");

  // Extend line to edges
  const firstY = keyframes.length > 0 ? (1 - keyframes[0].value) * height : 0;
  const lastY = keyframes.length > 0 ? (1 - keyframes[keyframes.length - 1].value) * height : 0;
  const fullPoints = `0,${firstY} ${points} ${width},${lastY}`;

  // Fill area under curve
  const fillPoints = `0,${height} 0,${firstY} ${points} ${width},${lastY} ${width},${height}`;

  const getTimeAndValue = useCallback(
    (clientX: number, clientY: number): { time: number; value: number } => {
      const svg = svgRef.current;
      if (!svg) return { time: 0, value: 1 };
      const rect = svg.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const time = Math.max(0, Math.min((x / width) * trackDuration, trackDuration));
      const value = Math.max(0, Math.min(1 - y / height, 1));
      return { time, value };
    },
    [width, height, trackDuration],
  );

  // Double-click to add a keyframe
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const { time, value } = getTimeAndValue(e.clientX, e.clientY);
      const newKeyframes = [...keyframes, { time, value }].sort((a, b) => a.time - b.time);
      onDispatch({
        type: "UPDATE_AUDIO_TRACK",
        id: track.id,
        patch: { volumeKeyframes: newKeyframes },
      });
    },
    [keyframes, track.id, onDispatch, getTimeAndValue],
  );

  // Start dragging a keyframe
  const handlePointMouseDown = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setDraggingIndex(index);
      startValuesRef.current = [...keyframes];

      const handleMouseMove = (e: MouseEvent) => {
        const { time, value } = getTimeAndValue(e.clientX, e.clientY);
        const updated = [...startValuesRef.current];
        updated[index] = { time, value };
        updated.sort((a, b) => a.time - b.time);
        onDispatch({
          type: "UPDATE_AUDIO_TRACK_PREVIEW",
          id: track.id,
          patch: { volumeKeyframes: updated },
        });
      };

      const handleMouseUp = (e: MouseEvent) => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        setDraggingIndex(null);

        const { time, value } = getTimeAndValue(e.clientX, e.clientY);
        const updated = [...startValuesRef.current];
        updated[index] = { time, value };
        updated.sort((a, b) => a.time - b.time);

        // Revert preview, then apply with undo
        onDispatch({
          type: "UPDATE_AUDIO_TRACK_PREVIEW",
          id: track.id,
          patch: { volumeKeyframes: startValuesRef.current },
        });
        onDispatch({
          type: "UPDATE_AUDIO_TRACK",
          id: track.id,
          patch: { volumeKeyframes: updated },
        });
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [keyframes, track.id, onDispatch, getTimeAndValue],
  );

  // Right-click to delete a keyframe (keep at least one)
  const handlePointContextMenu = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (keyframes.length <= 1) return;
      const newKeyframes = keyframes.filter((_, i) => i !== index);
      onDispatch({
        type: "UPDATE_AUDIO_TRACK",
        id: track.id,
        patch: { volumeKeyframes: newKeyframes },
      });
    },
    [keyframes, track.id, onDispatch],
  );

  return (
    <svg
      ref={svgRef}
      className="volume-keyframe-editor"
      width={width}
      height={height}
      onDoubleClick={handleDoubleClick}
    >
      {/* Fill under curve */}
      <polygon points={fillPoints} className="volume-keyframe-fill" />

      {/* Volume curve line */}
      <polyline
        points={fullPoints}
        className="volume-keyframe-line"
      />

      {/* Keyframe points */}
      {keyframes.map((kf, i) => {
        const x = (kf.time / trackDuration) * width;
        const y = (1 - kf.value) * height;
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={draggingIndex === i ? 5 : 4}
            className={`volume-keyframe-point${draggingIndex === i ? " dragging" : ""}`}
            onMouseDown={(e) => handlePointMouseDown(i, e)}
            onContextMenu={(e) => handlePointContextMenu(i, e)}
          />
        );
      })}
    </svg>
  );
}
